import type { Translate } from '@/i18n'
import { divideDecimals, multiplyDecimals } from '@/lib/format'
import type { PriceTier } from '@/services/finance/priceLists.service'
import type { OrderStatus, SalesOrder } from '@/services/sales/orders.service'
import { addAmounts, isAmount, isPositive } from '@/features/warehouse/documents/DocumentParts'

/** Kanban column order: the order's way from the agent to the client. */
export const ORDER_STATUSES: OrderStatus[] = [
  'agent',
  'moderator',
  'admin',
  'cashier',
  'accountant',
  'warehouse',
  'ready_to_shipment',
  'on_way',
  'completed',
  'canceled',
]

/** Legacy Update.jsx: the whole form is read-only once the order leaves the warehouse or is canceled. */
export const LOCKED_STATUSES: OrderStatus[] = ['ready_to_shipment', 'on_way', 'completed', 'canceled']

/**
 * filial: a «Филиал» client's order (sales_scheme branch) waits at status
 * agent for its «Менеджер», so it reads «У Менеджера» (#574).
 */
export const statusLabel = (status: string, t: Translate, filial = false) => {
  if (!status) return '—'
  if (status === 'agent' && filial) return t('sales.orders.statuses.manager')
  return t(`sales.orders.statuses.${status}` as 'sales.orders.statuses.agent')
}
/** A «Филиал» client's order: no agent, its «Менеджер» takes the agent's step. */
export const isFilialOrder = (order?: Pick<SalesOrder, 'sales_scheme'> | null) => order?.sales_scheme === 'branch'
export const paymentLabel = (value: string, t: Translate) =>
  value ? t(`sales.orders.payment.${value}` as 'sales.orders.payment.cash_card') : '—'
export const receiveLabel = (value: string, t: Translate) =>
  value ? t(`sales.orders.receive.${value}` as 'sales.orders.receive.pickup') : '—'
export const payerLabel = (value: string, t: Translate) =>
  value ? t(`sales.orders.payer.${value}` as 'sales.orders.payer.client_expense') : '—'
export const priceTypeLabel = (value: string, t: Translate) =>
  value ? t(`sales.orders.priceTypes.${value}` as 'sales.orders.priceTypes.standard') : '—'
export const invoiceLabel = (value: string, t: Translate) =>
  value ? t(`sales.orders.invoice.${value}` as 'sales.orders.invoice.new') : '—'

export const ORDER_CREATORS = ['client', 'agent', 'manager', 'call_center', 'staff'] as const
export const creatorOptions = (t: Translate) => ORDER_CREATORS.map((value) => ({ value, label: t(`sales.orders.creators.${value}`) }))
/** «Создал»: Клиент / Агент / Колл-центр, a staff member by name. */
export const creatorLabel = (order: Pick<SalesOrder, 'created_by_kind' | 'created_by_name'>, t: Translate) => {
  if (!order.created_by_kind) return '—'
  if (order.created_by_kind === 'staff') return order.created_by_name || t('sales.orders.creators.staff')
  return t(`sales.orders.creators.${order.created_by_kind}`)
}
/** The filter and board label of a status: agent covers «У агента» and «У Менеджера». */
export const statusGroupLabel = (status: string, t: Translate) =>
  status === 'agent' ? t('sales.orders.statuses.agentOrManager') : statusLabel(status, t)
export const statusOptions = (t: Translate) => ORDER_STATUSES.map((value) => ({ value, label: statusGroupLabel(value, t) }))
export const paymentOptions = (t: Translate) =>
  (['cash_card', 'transfer', 'credit'] as const).map((value) => ({ value, label: paymentLabel(value, t) }))
export const receiveOptions = (t: Translate) =>
  (['pickup', 'courier', 'yandex'] as const).map((value) => ({ value, label: receiveLabel(value, t) }))
export const payerOptions = (t: Translate) =>
  (['client_expense', 'agent_expense'] as const).map((value) => ({ value, label: payerLabel(value, t) }))
export const yesNoOptions = (t: Translate) => [
  { value: 'true', label: t('sales.orders.yes') },
  { value: 'false', label: t('sales.orders.no') },
]

export type OrderLineDraft = {
  key: string
  /** The saved line's id; empty for a new line. */
  id: string
  product_id: string
  product_name: string
  is_belarus: boolean
  is_uncut: boolean
  packaging_id: string
  weight: string
  count: string
  front: string
  rear: string
  sale_price: string
  client_price: string
}

export const emptyLine = (): OrderLineDraft => ({
  key: crypto.randomUUID(),
  id: '',
  product_id: '',
  product_name: '',
  is_belarus: false,
  is_uncut: false,
  packaging_id: '',
  weight: '',
  count: '',
  front: '0',
  rear: '0',
  sale_price: '',
  client_price: '',
})

export const isBelarusLine = (line: OrderLineDraft) => line.is_belarus && !line.is_uncut

/** price × kg, rounded half-up to tiyin as the totals show it. */
export function lineAmount(price: string, weight: string): string {
  const product = multiplyDecimals(price || '0', weight || '0')
  return product ? divideDecimals(product, '1', 2) || '0' : '0'
}

export function orderTotals(lines: OrderLineDraft[]) {
  return {
    front: addAmounts(...lines.map((line) => line.front)),
    rear: addAmounts(...lines.map((line) => line.rear)),
    count: addAmounts(...lines.map((line) => line.count)),
    weight: addAmounts(...lines.map((line) => line.weight)),
    sale: addAmounts(...lines.map((line) => lineAmount(line.sale_price, line.weight))),
    client: addAmounts(...lines.map((line) => lineAmount(line.client_price, line.weight))),
  }
}

/**
 * The range a line's weight falls in, [from, to), else the last range, as
 * the legacy form and the backend price lookup choose it.
 */
export function tierFor(tiers: PriceTier[] | undefined, weight: string): { index: number; price: string } | null {
  if (!tiers || tiers.length === 0) return null
  const kg = Number(weight) || 0
  const index = tiers.findIndex((tier) => {
    const from = Number(tier.from_weight) || 0
    const to = tier.to_weight ? Number(tier.to_weight) : Infinity
    return kg >= from && kg < (to || Infinity)
  })
  const chosen = index >= 0 ? index : tiers.length - 1
  return { index: chosen, price: tiers[chosen].price }
}

/** Legacy save validation (ClientOrder/Form/Create.jsx, Update.jsx) plus the quantity rules of the line. */
/** filial: a «Филиал» client's line needs a positive «Цена Клиент». */
export function validateLines(value: unknown, t: Translate, filial = false) {
  const lines = (value as OrderLineDraft[]) ?? []
  if (lines.length === 0) return t('sales.orders.validation.addProduct')
  for (const line of lines) {
    if (!line.product_id) return t('sales.orders.validation.selectProduct')
    if (isBelarusLine(line) && !isPositive(addAmounts(line.front, line.rear))) return t('sales.orders.validation.quarters')
    if (line.is_uncut && !isPositive(line.count)) return t('sales.orders.validation.count')
    if (!isPositive(line.weight)) return t('sales.orders.validation.weightPositive')
    if (!isAmount(line.sale_price) || !isAmount(line.client_price)) return t('sales.orders.validation.price')
    if (filial && !isPositive(line.client_price)) return t('sales.orders.validation.clientPricePositive')
  }
  return undefined
}
